import type { ReactNode } from 'react';
import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CollectionsIcon from '@mui/icons-material/Collections';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import { SoftButton } from '../../../shared/components/SoftButton';
import { formatAge } from '../../../shared/components/relativeTime';
import type { Document } from '../../../core/types/data-models';

/** Small icon+count pair used for the entities/visualizations/presentations stats on a row card. */
function StatIcon({ icon, count, label }: { icon: ReactNode; count: number; label: string }) {
  return (
    <Tooltip title={label}>
      <Stack direction="row" spacing={0.4} alignItems="center">
        {icon}
        <Typography variant="caption" color="text.secondary">
          {count}
        </Typography>
      </Stack>
    </Tooltip>
  );
}

export type AssistantDocStats = {
  reviewCount: number;
  commentCount: number;
  entityCount: number;
  visualizationCount: number;
  presentationCount: number;
};

/**
 * One document's card in the row layout: name, description, review/comment
 * status, last update, related-content counts, and a button to open it.
 */
export function AssistantDocumentCard({
  doc,
  stats,
  onOpen,
}: {
  doc: Document;
  stats: AssistantDocStats;
  onOpen: () => void;
}) {
  const { reviewCount, commentCount, entityCount, visualizationCount, presentationCount } = stats;
  return (
    <GlassPanel
      hover
      sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75, p: 1.5 }}
    >
      <Typography variant="body2" fontWeight={700} noWrap>
        {doc.name}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          lineHeight: 1.4,
          minHeight: '2.8em',
        }}
      >
        {doc.description || 'No description'}
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center">
        {reviewCount > 0 ? (
          <Tooltip title={`${reviewCount} section${reviewCount === 1 ? '' : 's'} in review`}>
            <Chip
              icon={<RateReviewOutlinedIcon sx={{ fontSize: 13 }} />}
              label={reviewCount}
              size="small"
              color="warning"
              sx={{ height: 20, fontSize: 10 }}
            />
          </Tooltip>
        ) : (
          <Tooltip title="No sections in review">
            <TaskAltIcon sx={{ fontSize: 16 }} color="success" />
          </Tooltip>
        )}
        <StatIcon
          icon={<ChatBubbleOutlineIcon sx={{ fontSize: 14 }} color={commentCount > 0 ? 'info' : 'disabled'} />}
          count={commentCount}
          label={`${commentCount} open comment${commentCount === 1 ? '' : 's'}`}
        />
        <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', whiteSpace: 'nowrap' }}>
          {formatAge(doc.updatedAt)}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center">
        <StatIcon
          icon={<AccountTreeIcon sx={{ fontSize: 14 }} color="action" />}
          count={entityCount}
          label={`${entityCount} related entit${entityCount === 1 ? 'y' : 'ies'}`}
        />
        <StatIcon
          icon={<CollectionsIcon sx={{ fontSize: 14 }} color="action" />}
          count={visualizationCount}
          label={`${visualizationCount} visualization${visualizationCount === 1 ? '' : 's'}`}
        />
        <StatIcon
          icon={<SlideshowIcon sx={{ fontSize: 14 }} color="action" />}
          count={presentationCount}
          label={`${presentationCount} presentation${presentationCount === 1 ? '' : 's'}`}
        />
      </Stack>

      <SoftButton tone="primary" size="small" onClick={onOpen} sx={{ alignSelf: 'flex-start', mt: 'auto' }}>
        Open
      </SoftButton>
    </GlassPanel>
  );
}
